import { Link } from "react-router-dom";
import { cardBorderClassName } from "../../lib/cardStyles";
import { DEMO_BOOKING_URL } from "../../lib/pricing";

const HEX_GRID_STYLE = `
  .hex-grid {
    background-image: 
      linear-gradient(30deg, rgba(138, 43, 226, 0.05) 12%, transparent 12.5%, transparent 87%, rgba(138, 43, 226, 0.05) 87.5%, rgba(138, 43, 226, 0.05)),
      linear-gradient(150deg, rgba(138, 43, 226, 0.05) 12%, transparent 12.5%, transparent 87%, rgba(138, 43, 226, 0.05) 87.5%, rgba(138, 43, 226, 0.05)),
      linear-gradient(30deg, rgba(138, 43, 226, 0.05) 12%, transparent 12.5%, transparent 87%, rgba(138, 43, 226, 0.05) 87.5%, rgba(138, 43, 226, 0.05)),
      linear-gradient(150deg, rgba(138, 43, 226, 0.05) 12%, transparent 12.5%, transparent 87%, rgba(138, 43, 226, 0.05) 87.5%, rgba(138, 43, 226, 0.05)),
      linear-gradient(60deg, rgba(255, 106, 0, 0.03) 25%, transparent 25.5%, transparent 75%, rgba(255, 106, 0, 0.03) 75%, rgba(255, 106, 0, 0.03)),
      linear-gradient(60deg, rgba(255, 106, 0, 0.03) 25%, transparent 25.5%, transparent 75%, rgba(255, 106, 0, 0.03) 75%, rgba(255, 106, 0, 0.03));
    background-size: 80px 140px;
    background-position: 0 0, 0 0, 40px 70px, 40px 70px, 0 0, 40px 70px;
  }
`;

export function FeatureNav() {
  return (
    <header className="relative z-20 border-b border-white/10 bg-[#0a0a12]/80 backdrop-blur sticky top-0">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 sm:px-6 py-4">
        <Link to="/" className="flex items-center gap-3">
          <div
            className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-500 to-orange-400"
            role="img"
            aria-label="NeuroSphere"
          />
          <div className="leading-tight">
            <div className="text-sm font-semibold tracking-wide" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
              NeuroSphere
            </div>
            <div className="hidden sm:block text-xs text-white/60" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
              Voice • Brain • Memory
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/pricing"
            className="hidden sm:inline-block rounded-xl border border-white/15 px-4 py-2 text-sm text-white/80 hover:border-white/30 hover:text-white"
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
          >
            Pricing
          </Link>
          <Link
            to="/login"
            className="hidden sm:inline-block rounded-xl border border-white/15 px-4 py-2 text-sm text-white/80 hover:border-white/30 hover:text-white"
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
          >
            Login
          </Link>
          <a
            href={DEMO_BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap rounded-xl bg-white px-3 sm:px-4 py-2 text-sm font-semibold text-[#0a0a12] hover:bg-white/90"
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
          >
            Book a demo
          </a>
        </div>
      </div>
    </header>
  );
}

export function FeatureFooter() {
  return (
    <footer className="relative z-10 border-t border-gray-800 py-8 text-center">
      <div className="flex justify-center gap-6 mb-4 text-sm text-gray-500">
        <Link to="/">Home</Link>
        <Link to="/pricing">Pricing</Link>
        <Link to="/login">Login</Link>
        <a href={DEMO_BOOKING_URL} target="_blank" rel="noopener noreferrer">Book a demo</a>
      </div>
      <p className="text-gray-500 text-sm mb-3" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
        © 2026 NeuroSphere. All rights reserved.
      </p>
      <div className="flex justify-center gap-6">
        <a href="#" className="text-gray-500 text-sm hover:text-gray-400" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
          Privacy Policy
        </a>
        <a href="#" className="text-gray-500 text-sm hover:text-gray-400" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
          Terms of Service
        </a>
      </div>
    </footer>
  );
}

export function FeaturePageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen w-full bg-transparent text-white relative overflow-hidden">
      <div className="absolute inset-0 hex-grid opacity-30" />
      <FeatureNav />
      <div className="relative z-10">{children}</div>
      <FeatureFooter />
      <style>{HEX_GRID_STYLE}</style>
    </div>
  );
}

export function FeatureHero({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="flex flex-col items-center pt-24 pb-16 px-6 text-center">
      <h1
        className="text-4xl md:text-6xl font-extrabold bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent mb-6 max-w-4xl"
        style={{
          fontFamily: "Orbitron, sans-serif",
          textShadow: "0 0 40px rgba(138, 43, 226, 0.3), 0 0 80px rgba(255, 106, 0, 0.2)",
        }}
      >
        {title}
      </h1>
      <p
        className="text-lg md:text-xl text-gray-300 max-w-3xl tracking-wide"
        style={{ fontFamily: "Space Grotesk, sans-serif" }}
      >
        {subtitle}
      </p>
    </div>
  );
}

export function FeatureDetailSection({
  icon,
  title,
  description,
}: {
  icon: string;
  title: string;
  description: string;
}) {
  return (
    <div
      className={`rounded-xl p-6 ${cardBorderClassName}`}
      style={{
        background: "rgba(28, 28, 37, 0.7)",
      }}
    >
      <i className={`${icon} text-3xl text-purple-400 mb-4 block`} />
      <h2
        className="text-xl font-semibold text-white mb-3"
        style={{ fontFamily: "Space Grotesk, sans-serif" }}
      >
        {title}
      </h2>
      <p
        className="text-gray-400 leading-relaxed"
        style={{ fontFamily: "Space Grotesk, sans-serif" }}
      >
        {description}
      </p>
    </div>
  );
}

export function FeatureCta({ headline }: { headline: string }) {
  return (
    <div className="flex flex-col items-center py-20 px-6 text-center">
      <h2
        className="text-2xl md:text-3xl font-bold text-white mb-8"
        style={{ fontFamily: "Space Grotesk, sans-serif" }}
      >
        {headline}
      </h2>
      <a
        href={DEMO_BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="px-10 py-4 rounded-lg bg-gradient-to-r from-purple-500 to-orange-400 hover:from-purple-600 hover:to-orange-500 transition-all duration-300 text-white font-semibold shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-105 whitespace-nowrap cursor-pointer"
        style={{ fontFamily: "Space Grotesk, sans-serif" }}
      >
        Book a demo
      </a>
      <Link
        to="/"
        className="mt-6 text-sm text-gray-400 hover:text-gray-300"
        style={{ fontFamily: "Space Grotesk, sans-serif" }}
      >
        ← Back
      </Link>
    </div>
  );
}
